/* ─────────────────────────────────────────────────────────────
   SCENARIO BUILDER — the artifact's Assumptions panel + "Save version" bar

     POST   .../intrinsic-value/scenario          what-if recompute (read-only)
     POST   .../intrinsic-value/scenarios         save a named version
     GET    .../intrinsic-value/scenarios         the caller's saved list
     DELETE .../intrinsic-value/scenarios/{id}    remove one
     POST   .../intrinsic-value/scenario/export   Excel workbook

   Inputs are left BLANK by default and show the stored value as placeholder
   text, so an untouched panel sends an empty body — which the API defines as
   "replay the stored result". Only fields the user actually types are sent, so
   a scenario never silently pins a value the user did not choose.

   Percent fields are entered as percentages (9.4) and sent as decimals
   (0.094), matching the API contract.

   Saved scenarios are keyed per user + stock + NAME, so saving an existing
   name overwrites it — the UI says so rather than letting it surprise anyone.
───────────────────────────────────────────────────────────── */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { Download, Save, RotateCcw, Trash2, Play } from "lucide-react";
import {
  computeIntrinsicValueScenario,
  saveIntrinsicValueScenario,
  getIntrinsicValueScenarios,
  deleteIntrinsicValueScenario,
  exportIntrinsicValueScenario,
} from "../../../api/stocks_api";
import { formatCurrency, fmtL } from "../../../utils/currency";
import {
  scenarioFieldsFor,
  decimalAsPct,
  methodLabel,
  fmtAsOf,
  anchorTreatmentList,
  anchorTreatmentBody,
  noFadeIsApproximate,
  NO_FADE_PRECISION_NOTE,
} from "../../../config/valuation";
import { fmtNum } from "../../../config/earningsNote";
import {
  SectionTitle, Card, TileGrid, Banner, Pill,
  Scroller, Table, Th, Td, Tl, toneColor,
} from "./earningsNoteUI";
import SensitivityGrid from "./sensitivityGrid";

/* ── Small labelled numeric input ── */
const Field = ({ label, suffix, hint, value, placeholder, onChange, disabled }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        color: "var(--color-text-muted)",
        fontSize: "var(--font-size-xs)",
        fontWeight: "var(--font-weight-semibold)",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
      {hint && (
        <MuiTooltip title={hint} placement="top" arrow enterDelay={250}>
          <span style={{ cursor: "help", opacity: 0.7 }}>ⓘ</span>
        </MuiTooltip>
      )}
    </span>
    <span style={{ position: "relative", display: "flex", alignItems: "center" }}>
      <input
        type="number"
        step="any"
        inputMode="decimal"
        value={value}
        placeholder={placeholder ?? ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: suffix ? "7px 26px 7px 10px" : "7px 10px",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--color-border-primary)",
          backgroundColor: "var(--color-bg-secondary)",
          color: "var(--color-text-primary)",
          fontSize: "var(--font-size-sm)",
          fontWeight: "var(--font-weight-semibold)",
          fontVariantNumeric: "tabular-nums",
        }}
      />
      {suffix && (
        <span
          style={{
            position: "absolute",
            right: 9,
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-xs)",
            pointerEvents: "none",
          }}
        >
          {suffix}
        </span>
      )}
    </span>
  </label>
);

const btn = (variant) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  borderRadius: "var(--radius-lg)",
  fontSize: "var(--font-size-sm)",
  fontWeight: "var(--font-weight-semibold)",
  cursor: "pointer",
  transition: "var(--transition-fast)",
  border:
    variant === "primary" ? "1px solid var(--color-primary)" : "1px solid var(--color-border-primary)",
  backgroundColor: variant === "primary" ? "var(--color-primary)" : "var(--color-surface)",
  color: variant === "primary" ? "#FFFFFF" : "var(--color-text-secondary)",
});

const ScenarioBuilder = ({ ticker, intrinsicValue, dataAsOf, anchorTreatments }) => {
  const method = intrinsicValue?.calculation_method;
  const anchor = intrinsicValue?.consensus_anchor ?? null;
  const fields = useMemo(() => scenarioFieldsFor(method), [method]);

  /* The three anchor treatments, already priced on the IV GET. An empty list
     means this name has no anchor to vary — the control hides. */
  const treatments = useMemo(() => anchorTreatmentList(anchorTreatments), [anchorTreatments]);
  const approxNoFade = noFadeIsApproximate(anchor?.fade_den);
  const inputsByName = useMemo(() => {
    const out = {};
    (dataAsOf?.inputs ?? []).forEach((i) => {
      out[i.name] = i.value;
    });
    return out;
  }, [dataAsOf]);

  /* Raw string state per field — blank means "not overridden". */
  const [values, setValues] = useState({});
  /* Which anchor treatment the forecast runs on — "base" sends nothing. */
  const [treatment, setTreatment] = useState("base");
  const [fcfeOverrides, setFcfeOverrides] = useState({});

  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [notice, setNotice] = useState(null);

  const [name, setName] = useState("");
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(true);

  const projectedYears = useMemo(
    () => (intrinsicValue?.projected_fcfe ?? []).map((r) => r.fiscal_year).filter(Boolean),
    [intrinsicValue]
  );

  /* ── Assemble the request body from touched fields only ── */
  const assumptions = useMemo(() => {
    const body = {};
    fields.forEach((f) => {
      const raw = values[f.key];
      if (raw === undefined || raw === "" || raw === null) return;
      const n = Number(raw);
      if (Number.isNaN(n)) return;
      body[f.key] = f.kind === "pct" ? n / 100 : n;
    });
    /* base sends nothing (the API replays the stored result); no_fade sends
       {fade:false}; no_anchor sends {consensus_anchor:false}. */
    Object.assign(body, anchorTreatmentBody(treatment));

    const overrides = {};
    Object.entries(fcfeOverrides).forEach(([year, raw]) => {
      if (raw === "" || raw == null) return;
      const n = Number(raw);
      if (!Number.isNaN(n)) overrides[year] = n;
    });
    if (Object.keys(overrides).length) body.fcfe_overrides = overrides;

    return body;
  }, [fields, values, treatment, fcfeOverrides]);

  const dirty = Object.keys(assumptions).length > 0;

  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    const res = await getIntrinsicValueScenarios(ticker);
    setSaved(res?.success && Array.isArray(res.data) ? res.data : []);
    setSavedLoading(false);
  }, [ticker]);

  useEffect(() => {
    if (!ticker) return;
    loadSaved();
  }, [ticker, loadSaved]);

  const run = async () => {
    setBusy(true);
    setProblem(null);
    setNotice(null);
    const res = await computeIntrinsicValueScenario(ticker, assumptions);
    if (res?.success) setResult(res.data);
    else setProblem(res?.detail ?? "Could not compute this scenario.");
    setBusy(false);
  };

  const reset = () => {
    setValues({});
    setTreatment("base");
    setFcfeOverrides({});
    setResult(null);
    setProblem(null);
    setNotice(null);
  };

  const save = async () => {
    if (!name.trim()) {
      setProblem("Give the version a name before saving.");
      return;
    }
    setBusy(true);
    setProblem(null);
    const res = await saveIntrinsicValueScenario(ticker, name.trim(), assumptions);
    if (res?.success) {
      setNotice(`Saved as “${name.trim()}”.`);
      setName("");
      await loadSaved();
    } else {
      setProblem(res?.detail ?? "Could not save this version.");
    }
    setBusy(false);
  };

  const download = async () => {
    setBusy(true);
    setProblem(null);
    setNotice(null);
    const res = await exportIntrinsicValueScenario(ticker, assumptions);
    if (res?.success) setNotice(`Downloaded ${res.filename}.`);
    else setProblem("Could not export this scenario.");
    setBusy(false);
  };

  const loadScenario = (sc) => {
    const next = {};
    Object.entries(sc?.assumptions ?? {}).forEach(([k, v]) => {
      if (k === "consensus_anchor" || k === "fade" || k === "fcfe_overrides") return;
      const field = fields.find((f) => f.key === k);
      if (!field) return;
      next[k] = field.kind === "pct" ? String(Number(v) * 100) : String(v);
    });
    setValues(next);
    /* The saved body carries the treatment implicitly — map it back. */
    setTreatment(
      sc?.assumptions?.consensus_anchor === false
        ? "no_anchor"
        : sc?.assumptions?.fade === false
        ? "no_fade"
        : "base"
    );
    setFcfeOverrides(
      Object.fromEntries(
        Object.entries(sc?.assumptions?.fcfe_overrides ?? {}).map(([y, v]) => [y, String(v)])
      )
    );
    setResult(null);
    setNotice(`Loaded “${sc?.name}” — press Recompute to run it.`);
  };

  const remove = async (id) => {
    setBusy(true);
    const res = await deleteIntrinsicValueScenario(ticker, id);
    if (res?.success) await loadSaved();
    else setProblem("Could not delete that version.");
    setBusy(false);
  };

  /* ── Result tiles ── */
  const tiles = result
    ? [
        result.iv_per_share != null
          ? { label: "Scenario TV / share", value: formatCurrency(result.iv_per_share) }
          : null,
        result.stored_iv_per_share != null
          ? { label: "Stored TV / share", value: formatCurrency(result.stored_iv_per_share), tone: "muted" }
          : null,
        result.delta_vs_stored != null
          ? {
              label: "Δ vs stored",
              // Sign goes OUTSIDE the currency symbol — formatCurrency on a
              // negative number would render "$-11.05".
              value: `${Number(result.delta_vs_stored) >= 0 ? "+" : "−"}${formatCurrency(
                Math.abs(Number(result.delta_vs_stored))
              )}`,
              tone: Number(result.delta_vs_stored) >= 0 ? "pos" : "neg",
            }
          : null,
        result.current_price != null
          ? { label: "Current price", value: formatCurrency(result.current_price), tone: "muted" }
          : null,
        result.discount_rate != null
          ? { label: "Discount rate", value: decimalAsPct(result.discount_rate, 2) }
          : null,
        result.terminal_growth != null
          ? { label: "Terminal growth", value: decimalAsPct(result.terminal_growth, 2) }
          : null,
        result.tv_share_of_value != null
          ? {
              label: "Terminal-value reliance",
              value: decimalAsPct(result.tv_share_of_value, 0),
              tone: Number(result.tv_share_of_value) >= 0.75 ? "warn" : "neutral",
            }
          : null,
        result.continuation_years != null
          ? { label: "Continuation years", value: fmtNum(result.continuation_years, 0) }
          : null,
        result.pv_projected != null
          ? { label: "PV of projections", value: formatCurrency(result.pv_projected, { compact: true }) }
          : null,
        result.pv_terminal != null
          ? { label: "PV of terminal value", value: formatCurrency(result.pv_terminal, { compact: true }) }
          : null,
      ].filter(Boolean)
    : [];

  return (
    <>
      <SectionTitle caption="change the assumptions and recompute — nothing is stored unless you save a version">
        Assumptions
      </SectionTitle>

      <Card>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
            gap: "var(--spacing-md)",
          }}
        >
          {fields.map((f) => (
            <Field
              key={f.key}
              label={f.label}
              hint={f.hint}
              suffix={f.kind === "pct" ? "%" : undefined}
              value={values[f.key] ?? ""}
              placeholder={(() => {
                const stored = f.storedFrom?.(intrinsicValue, inputsByName);
                if (stored == null) return "";
                return f.kind === "pct" ? fmtL(Number(stored) * 100, 2) : fmtL(stored, 3);
              })()}
              onChange={(v) => setValues((p) => ({ ...p, [f.key]: v }))}
              disabled={busy}
            />
          ))}
        </div>

        {/* ── Forecast anchor ──
            All three treatments come priced on the IV GET (`anchor_treatments`)
            and all three are requestable: base sends an empty body, no-fade
            sends {fade:false}, off sends {consensus_anchor:false}. A null
            `anchor_treatments` means there is no anchor to vary at all (the
            asset-manager engine, unanchored/fallback rows) — the picker is
            hidden and the note explains why. */}
        <div style={{ marginTop: "var(--spacing-md)" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 6,
            }}
          >
            <span
              style={{
                color: "var(--color-text-primary)",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-bold)",
              }}
            >
              Forecast anchor
            </span>
            <Pill>{anchor?.on ? "analyst consensus is the base model" : "base model"}</Pill>
          </div>

          {treatments.length > 0 ? (
            <>
              <select
                value={treatment}
                onChange={(e) => setTreatment(e.target.value)}
                disabled={busy}
                aria-label="Forecast anchor"
                style={{
                  padding: "8px 10px",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--color-border-primary)",
                  backgroundColor: "var(--color-bg-secondary)",
                  color: "var(--color-text-primary)",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  minWidth: 320,
                }}
              >
                {treatments.map((t) => (
                  /* A null iv_per_share means that leg is not computable for
                     this name — offered but disabled, never shown as 0. */
                  <option key={t.key} value={t.key} disabled={!t.available}>
                    {t.label}
                    {t.key === "base" ? " (base)" : ""}
                    {t.available
                      ? ` — ${formatCurrency(t.iv_per_share)}`
                      : " — not computable"}
                  </option>
                ))}
              </select>

              <div
                style={{
                  marginTop: 6,
                  color: "var(--color-text-muted)",
                  fontSize: "var(--font-size-xs)",
                  lineHeight: 1.6,
                }}
              >
                {anchor?.eps_fy1 != null && (
                  <>
                    Consensus FY1 EPS {formatCurrency(anchor.eps_fy1)}
                    {anchor.eps_fy2 != null && <> · FY2 {formatCurrency(anchor.eps_fy2)}</>}.{" "}
                  </>
                )}
                Years 1–2 are anchored to consensus, then fade to the model’s own path.{" "}
                {anchor?.mom_high
                  ? "High momentum, so the fade is extended — the terminal year keeps half the year-2 anchor."
                  : "Below the 1.08 momentum gate, so the anchor fully reverts by the terminal year."}{" "}
                Momentum sets only the fade length, never the cash flows. Holding consensus with no
                fade leaves years 1–2 unchanged — they are already pinned to consensus EPS, and the
                fade only governs year 3 onwards.
              </div>

              {treatment === "no_fade" && approxNoFade && (
                <Banner kind="warning" style={{ marginTop: "var(--spacing-sm)" }}>
                  {NO_FADE_PRECISION_NOTE}
                </Banner>
              )}
            </>
          ) : (
            <div
              style={{
                color: "var(--color-text-muted)",
                fontSize: "var(--font-size-xs)",
                lineHeight: 1.6,
              }}
            >
              No analyst consensus is available for this name, so it is valued on the base model —
              the company’s own history-driven forecast, with no anchor. The consensus anchor is
              applied automatically wherever estimates exist.
            </div>
          )}
        </div>

        {/* per-year FCFE overrides */}
        {projectedYears.length > 0 && (
          <div style={{ marginTop: "var(--spacing-md)" }}>
            <div
              style={{
                color: "var(--color-text-muted)",
                fontSize: "var(--font-size-xs)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 8,
              }}
            >
              Pin FCFE by year (absolute, leave blank to keep the model’s value)
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "var(--spacing-sm)",
              }}
            >
              {projectedYears.map((y) => {
                const modelled = (intrinsicValue?.projected_fcfe ?? []).find(
                  (r) => r.fiscal_year === y
                )?.fcfe;
                return (
                  <Field
                    key={y}
                    label={`FY${y}`}
                    value={fcfeOverrides[y] ?? ""}
                    placeholder={modelled != null ? String(Math.round(modelled)) : ""}
                    onChange={(v) => setFcfeOverrides((p) => ({ ...p, [y]: v }))}
                    disabled={busy}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* actions */}
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            flexWrap: "wrap",
            alignItems: "center",
            marginTop: "var(--spacing-md)",
            paddingTop: "var(--spacing-md)",
            borderTop: "1px solid var(--color-border-primary)",
          }}
        >
          <button type="button" onClick={run} disabled={busy} style={btn("primary")}>
            <Play size={14} />
            {busy ? "Working…" : "Recompute"}
          </button>
          <button type="button" onClick={download} disabled={busy} style={btn()}>
            <Download size={14} />
            Excel
          </button>
          {dirty && (
            <button type="button" onClick={reset} disabled={busy} style={btn()}>
              <RotateCcw size={14} />
              Reset
            </button>
          )}
          {!dirty && (
            <Pill>No overrides — recompute replays the stored result</Pill>
          )}
        </div>

        {/* save-version bar */}
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-sm)",
            flexWrap: "wrap",
            alignItems: "center",
            marginTop: "var(--spacing-sm)",
          }}
        >
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Version name (e.g. bear case)"
            disabled={busy}
            style={{
              flex: "1 1 200px",
              minWidth: 160,
              padding: "8px 10px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--color-border-primary)",
              backgroundColor: "var(--color-bg-secondary)",
              color: "var(--color-text-primary)",
              fontSize: "var(--font-size-sm)",
            }}
          />
          <button type="button" onClick={save} disabled={busy} style={btn()}>
            <Save size={14} />
            Save version
          </button>
          <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}>
            Saving an existing name overwrites that version.
          </span>
        </div>
      </Card>

      {problem && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-sm)" }}>
          {problem}
        </Banner>
      )}
      {notice && (
        <Banner kind="success" style={{ marginTop: "var(--spacing-sm)" }}>
          {notice}
        </Banner>
      )}

      {/* ── Scenario result ── */}
      {result && (
        <div style={{ marginTop: "var(--spacing-md)" }}>
          <TileGrid tiles={tiles} min={165} />

          <div
            style={{
              marginTop: "var(--spacing-sm)",
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-xs)",
              lineHeight: 1.6,
            }}
          >
            {result.calculation_method && <>Engine: {methodLabel(result.calculation_method)}. </>}
            {result.discount_rate_source && <>Cost of equity from {String(result.discount_rate_source).replace(/\s*\(.*\)\s*$/, "")}. </>}
            {result.shares_used != null && (
              <>Diluted shares {fmtL(result.shares_used / 1e9, 2)}B. </>
            )}
            This is a what-if calculation — nothing is stored unless you save a version.
          </div>

          {Array.isArray(result.years) && result.years.length > 0 && (
            <Scroller style={{ marginTop: "var(--spacing-sm)" }}>
              <Table>
                <thead>
                  <tr>
                    <Th left>Fiscal year</Th>
                    <Th>FCFE</Th>
                    <Th>t (mid)</Th>
                    <Th>PV</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.years.map((y, i) => (
                    <tr key={y.fiscal_year ?? i}>
                      <Tl>FY{y.fiscal_year ?? "—"}</Tl>
                      <Td bold>{y.fcfe != null ? formatCurrency(y.fcfe, { compact: true }) : "—"}</Td>
                      <Td tone="muted">{y.t_mid != null ? fmtL(y.t_mid, 3) : "—"}</Td>
                      <Td>{y.pv != null ? formatCurrency(y.pv, { compact: true }) : "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Scroller>
          )}

          <SensitivityGrid
            grid={result.sensitivity_grid}
            centreValue={result.iv_per_share}
            title="Scenario sensitivity grid"
            caption="recentred on this scenario’s assumptions"
          />
        </div>
      )}

      {/* ── Saved versions ── */}
      {!savedLoading && saved.length > 0 && (
        <>
          <SectionTitle caption="your saved versions for this stock — newest first">
            Saved versions
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Name</Th>
                  <Th>TV / share</Th>
                  <Th left>Assumptions</Th>
                  <Th left>Saved</Th>
                  <Th left>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {saved.map((sc) => {
                  const iv = sc?.result?.iv_per_share;
                  const keys = Object.entries(sc?.assumptions ?? {});
                  return (
                    <tr key={sc.id}>
                      <Tl>{sc.name}</Tl>
                      <Td bold>{iv != null ? formatCurrency(iv) : "—"}</Td>
                      <Td left tone="muted" wrap style={{ width: "100%" }}>
                        {keys.length === 0
                          ? "stored assumptions"
                          : keys
                              .map(([k, v]) =>
                                typeof v === "object" && v !== null
                                  ? `${k}: ${Object.keys(v).length} year(s)`
                                  : `${k}: ${typeof v === "boolean" ? String(v) : fmtL(v, 4)}`
                              )
                              .join(" · ")}
                      </Td>
                      <Td left tone="muted">{sc.created_at ? fmtAsOf(sc.created_at) : "—"}</Td>
                      <Td left>
                        <span style={{ display: "inline-flex", gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => loadScenario(sc)}
                            disabled={busy}
                            style={{ ...btn(), padding: "4px 10px" }}
                          >
                            Load
                          </button>
                          <button
                            type="button"
                            onClick={() => remove(sc.id)}
                            disabled={busy}
                            aria-label={`Delete ${sc.name}`}
                            style={{ ...btn(), padding: "4px 10px", color: toneColor("neg") }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </span>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
        </>
      )}
    </>
  );
};

export default ScenarioBuilder;
